// src/pages/customer-panel/users/UsersPage.js
// User Management Page - Admin only

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    Snackbar,
    InputAdornment,
    Tooltip,
    Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People';
import LockIcon from '@mui/icons-material/Lock';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const ROLE_COLORS = {
    admin: { bg: '#f3e8ff', color: '#9333ea' },
    employee: { bg: '#dbeafe', color: '#2563eb' },
    customer: { bg: '#d1fae5', color: '#059669' },
    student: { bg: '#ffedd5', color: '#ea580c' },
};

const getRoleChipProps = (role) => {
    const config = ROLE_COLORS[role] || ROLE_COLORS.customer;
    return { bgcolor: config.bg, color: config.color };
};

const INITIAL_FORM = { name: '', email: '', role: 'customer' };

export default function UsersPage() {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team || 'customer';
    const isAdmin = userRole === 'admin';

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingUser, setDeletingUser] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get('/users', {
                params: {
                    search,
                    page: page + 1,
                    limit: rowsPerPage,
                },
            });
            const data = response.data;
            setUsers(data.data || data.users || []);
            setTotalCount(data.total || data.totalCount || 0);
        } catch (err) {
            console.error('Failed to fetch users:', err);
            setError(err.response?.data?.message || 'Failed to load users.');
        } finally {
            setLoading(false);
        }
    }, [search, page, rowsPerPage]);

    useEffect(() => {
        document.title = 'Users | Customer Panel';
        if (isAdmin) {
            fetchUsers();
        }
    }, [fetchUsers, isAdmin]);

    const handleOpenDialog = (userToEdit) => {
        if (userToEdit) {
            setEditingUser(userToEdit);
            setFormData({
                name: userToEdit.name || `${userToEdit.first_name || ''} ${userToEdit.last_name || ''}`.trim(),
                email: userToEdit.email || '',
                role: userToEdit.role || userToEdit.user_role || 'customer',
            });
        } else {
            setEditingUser(null);
            setFormData(INITIAL_FORM);
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingUser(null);
        setFormData(INITIAL_FORM);
    };

    const handleSubmit = async () => {
        if (!formData.name.trim() || !formData.email.trim()) {
            setSnackbar({ open: true, message: 'Name and email are required.', severity: 'warning' });
            return;
        }
        setSubmitting(true);
        try {
            if (editingUser) {
                await axiosInstance.put(`/users/${editingUser.id || editingUser._id}`, formData);
                setSnackbar({ open: true, message: 'User updated successfully!', severity: 'success' });
            } else {
                await axiosInstance.post('/users', formData);
                setSnackbar({ open: true, message: 'User added successfully!', severity: 'success' });
            }
            handleCloseDialog();
            fetchUsers();
        } catch (err) {
            console.error('Failed to save user:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to save user.',
                severity: 'error',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deletingUser) return;
        try {
            await axiosInstance.delete(`/users/${deletingUser.id || deletingUser._id}`);
            setSnackbar({ open: true, message: 'User deleted successfully!', severity: 'success' });
            setDeleteDialogOpen(false);
            setDeletingUser(null);
            fetchUsers();
        } catch (err) {
            console.error('Failed to delete user:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to delete user.',
                severity: 'error',
            });
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '--';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        } catch {
            return '--';
        }
    };

    // Not admin - show access denied
    if (!isAdmin) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
                <Paper
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        borderRadius: '16px',
                        border: '1px solid',
                        borderColor: 'grey.100',
                    }}
                >
                    <LockIcon sx={{ fontSize: 64, color: '#e2e8f0', mb: 2 }} />
                    <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>
                        Access Restricted
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily, color: '#64748b' }}>
                        You need admin privileges to manage users.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        Users
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                        Manage team members and their roles
                    </Typography>
                </Box>
                <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={() => handleOpenDialog(null)}
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        bgcolor: GREEN_ACCENT,
                        borderRadius: '10px',
                        textTransform: 'none',
                        px: 3,
                        '&:hover': { bgcolor: '#059669' },
                    }}
                >
                    Add User
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>
                    {error}
                </Alert>
            )}

            {/* Search */}
            <Paper
                sx={{
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    <TextField
                        placeholder="Search users..."
                        size="small"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        sx={{
                            flex: 1,
                            maxWidth: 400,
                            '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchUsers} sx={{ color: '#64748b' }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Table */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                {['Name', 'Email', 'Role', 'Type', 'Status', 'Created', 'Actions'].map((header) => (
                                    <TableCell
                                        key={header}
                                        sx={{
                                            fontFamily,
                                            fontWeight: 700,
                                            color: '#64748b',
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            borderBottom: '1px solid',
                                            borderColor: 'grey.100',
                                        }}
                                    >
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                        <CircularProgress size={32} sx={{ color: GREEN_ACCENT }} />
                                    </TableCell>
                                </TableRow>
                            ) : users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                        <PeopleIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No users found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                users.map((u) => {
                                    const name = u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '--';
                                    const role = u.role || u.user_role || 'customer';
                                    const roleChip = getRoleChipProps(role);
                                    return (
                                        <TableRow
                                            key={u.id || u._id}
                                            sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            fontSize: '0.8rem',
                                                            fontFamily,
                                                            fontWeight: 700,
                                                            bgcolor: GREEN_ACCENT,
                                                        }}
                                                    >
                                                        {name.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    {name}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                                {u.email || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={role.charAt(0).toUpperCase() + role.slice(1)}
                                                    size="small"
                                                    sx={{
                                                        fontFamily,
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        bgcolor: roleChip.bgcolor,
                                                        color: roleChip.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={u.is_crm_customer ? 'CRM Customer' : (u.is_external_invited ? 'External' : 'Internal')}
                                                    size="small"
                                                    sx={{
                                                        fontFamily,
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                        bgcolor: u.is_crm_customer ? '#fef3c7' : (u.is_external_invited ? '#e0e7ff' : '#f0fdf4'),
                                                        color: u.is_crm_customer ? '#d97706' : (u.is_external_invited ? '#4f46e5' : '#16a34a'),
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={u.status === 'active' || u.is_active ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    sx={{
                                                        fontFamily,
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        bgcolor: (u.status === 'active' || u.is_active) ? '#d1fae5' : '#fee2e2',
                                                        color: (u.status === 'active' || u.is_active) ? '#059669' : '#dc2626',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                                {formatDate(u.created_at || u.createdAt)}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenDialog(u)}
                                                        sx={{ color: '#64748b', mr: 0.5 }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => { setDeletingUser(u); setDeleteDialogOpen(true); }}
                                                        sx={{ color: '#ef4444' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    sx={{ '& *': { fontFamily } }}
                />
            </Paper>

            {/* Add/Edit User Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {editingUser ? 'Edit User' : 'Add New User'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        <TextField
                            label="Full Name"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            value={formData.email}
                            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                        <FormControl fullWidth>
                            <InputLabel sx={{ fontFamily }}>Role</InputLabel>
                            <Select
                                value={formData.role}
                                label="Role"
                                onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
                                sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                <MenuItem value="admin" sx={{ fontFamily }}>Admin</MenuItem>
                                <MenuItem value="employee" sx={{ fontFamily }}>Employee</MenuItem>
                                <MenuItem value="customer" sx={{ fontFamily }}>Customer</MenuItem>
                                <MenuItem value="student" sx={{ fontFamily }}>Student</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={handleCloseDialog}
                        sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            bgcolor: GREEN_ACCENT,
                            borderRadius: '10px',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#059669' },
                        }}
                    >
                        {editingUser ? 'Update' : 'Add User'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        Are you sure you want to delete{' '}
                        <strong>{deletingUser?.name || deletingUser?.email || 'this user'}</strong>?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDeleteConfirm}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            bgcolor: '#ef4444',
                            borderRadius: '10px',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#dc2626' },
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
